import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

interface TermsOfServiceDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function TermsOfServiceDialog({ open, onClose }: TermsOfServiceDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80vh',
            backgroundImage: 'none',
          },
        },
      }}
    >
      <DialogTitle>Terms of Service</DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" gutterBottom>
          Not Financial Advice
        </Typography>
        <Typography paragraph>
          The information, tools, and backtesting results provided by Orange Trie ("the Service") are for informational and educational purposes only. They do not constitute investment advice, financial advice, trading advice, or any other form of advice, and should not be relied upon as the basis for any investment decision.
        </Typography>
        <Typography paragraph>
          Orange Trie is not a registered investment adviser, broker-dealer, or financial planner, and nothing on this Service should be construed as a recommendation to buy, sell, or hold any security, financial instrument, or asset.
        </Typography>

        <Typography variant="h6" gutterBottom>
          Backtested Results Have Inherent Limitations
        </Typography>
        <Typography paragraph>
          Backtested performance results are hypothetical and do not represent actual trading. They are achieved through the retroactive application of a strategy designed, in part, with the benefit of hindsight. Backtested results are subject to inherent limitations, including but not limited to:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              Hindsight bias - strategies are often built or refined using knowledge of how the market actually performed, which is not available in real-time trading.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              Overfitting - a strategy that performs well on historical data may be tuned too closely to that specific dataset and fail to perform similarly on future or live data.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              No guarantee of future results - past performance, whether actual or backtested, is not indicative of future results.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 0 }}>
              Data and execution assumptions - backtests may not fully account for slippage, liquidity constraints, transaction costs, taxes, or other real-world trading frictions, and results may differ materially from what an investor would have actually achieved.
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" gutterBottom>
          Risk of Loss
        </Typography>
        <Typography paragraph>
          Trading and investing in financial markets involves substantial risk of loss and is not suitable for every investor. You are solely responsible for any investment or trading decisions you make, and for evaluating the merits and risks associated with the use of any information or tools provided by the Service. Orange Trie disclaims all liability for any loss or damage arising from reliance on information provided through the Service.
        </Typography>
        <Typography paragraph>
          You should consult a qualified financial advisor, accountant, or attorney before making any investment decision.
        </Typography>
        <Typography paragraph>
          Orange Trie does not review, endorse, verify, or guarantee the accuracy of any user-generated content. Shared strategies and results reflect the views of the individual user, not Orange Trie, and should not be treated as advice or a recommendation from Orange Trie or any other user.
        </Typography>

        <Typography variant="h6" gutterBottom>
          Beta Service
        </Typography>
        <Typography paragraph>
          Orange Trie is currently offered as a beta product. By using the Service, you acknowledge and agree that:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              The Service is under active development and may contain bugs, errors, or inaccuracies.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              Historical or market data used in backtesting may be incomplete, delayed, or contain errors, and Orange Trie makes no guarantee as to its accuracy or completeness.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              Features, functionality, and availability may change, be added, or be removed at any time without notice.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 1 }}>
              The Service may experience downtime, interruptions, or data loss, and Orange Trie does not guarantee uninterrupted or error-free operation during the beta period.
            </Typography>
          </li>
          <li>
            <Typography paragraph sx={{ mb: 0 }}>
              Backtesting results generated during the beta period may be affected by the issues described above and should be independently verified before being relied upon for any purpose.
            </Typography>
          </li>
        </Box>

        <Typography paragraph sx={{ mb: 0 }}>
          Orange Trie is not liable for any loss, damage, or inconvenience resulting from use of the Service during the beta period, to the fullest extent permitted by law.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

